/**
 * The till PIN: what lets a cashier unlock this device with no internet.
 *
 * It guards the device only. It proves nothing to the server and opens nothing
 * there, which is why sync still needs a real sign-in. Stored as a salted
 * PBKDF2 hash, never the digits, with a lockout after repeated wrong tries.
 */
const ITERATIONS = 150_000;
export const PIN_LENGTH_MIN = 4;
export const PIN_LENGTH_MAX = 8;
export const MAX_PIN_ATTEMPTS = 5;
export const PIN_LOCK_MS = 5 * 60 * 1000;

const toHex = (b: ArrayBuffer | Uint8Array) =>
  Array.from(b instanceof Uint8Array ? b : new Uint8Array(b), (x) =>
    x.toString(16).padStart(2, '0'),
  ).join('');
const fromHex = (h: string) =>
  Uint8Array.from(h.match(/.{2}/g) ?? [], (x) => Number.parseInt(x, 16));

export const isValidPin = (pin: string) =>
  new RegExp(`^\\d{${PIN_LENGTH_MIN},${PIN_LENGTH_MAX}}$`).test(pin);

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    key,
    256,
  );
  return toHex(bits);
}

export async function hashPin(
  pin: string,
): Promise<{ salt: string; hash: string; iterations: number }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { salt: toHex(salt), hash: await derive(pin, salt, ITERATIONS), iterations: ITERATIONS };
}

export async function pinMatches(
  pin: string,
  stored: { salt: string; hash: string; iterations: number },
): Promise<boolean> {
  return (await derive(pin, fromHex(stored.salt), stored.iterations)) === stored.hash;
}
